import React, { Fragment, useEffect, useMemo, useState } from "react";
import { usePersistentState } from "../Common/usePersistentFilters";
import {
  Badge,
  Button,
  Card,
  Col,
  Form,
  Modal,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import { useSearchParams } from "react-router-dom";
import swal from "sweetalert";
import api from "../../../api";
import PageTitle from "../../layouts/PageTitle";
import AppPagination from "../Common/AppPagination";
import ReceiptActions from "../Common/ReceiptActions";
import SignedBillActions from "../Common/SignedBillActions";

const money = (value) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
  }).format(Number(value || 0));

const getPaymentRows = (record) => {
  const payments = Array.isArray(record?.payments) ? [...record.payments] : [];
  const paidTotal = Number(record?.paidAmount || 0);
  const recordedTotal = payments.reduce((sum, payment) => sum + Number(payment.amount || 0), 0);
  const legacyAmount = paidTotal - recordedTotal;
  if (legacyAmount > 0.005) {
    payments.unshift({
      _id: `legacy-${record._id}`,
      amount: legacyAmount,
      paymentReference: record.paymentReference,
      paymentRemarks: record.paymentRemarks,
      paymentDate: record.paymentDate || record.updatedAt,
      receipt: record.receipt,
      additionalPaymentType: record.additionalPaymentType || "",
      additionalPaymentAmount: record.additionalPaymentAmount || 0,
      additionalPaymentReceipt: record.additionalPaymentReceipt,
      legacy: true,
    });
  }
  return payments;
};

const statusVariant = (status) => {
  if (status === "Paid") return "success";
  if (status === "Partially Paid") return "warning";
  if (status === "Rejected") return "danger";
  return "info";
};

const PurchaseOrderPaymentList = () => {
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [workOrders, setWorkOrders] = useState([]);
  const [projects, setProjects] = useState([]);
  const [searchParams] = useSearchParams();
  const workOrderFromUrl = searchParams.get("workOrder") || "";
  const projectFromUrl = searchParams.get("project") || "";
  const searchFromUrl = searchParams.get("search") || "";
  const [selectedProject, setSelectedProject] = usePersistentState("po_pay_project", projectFromUrl);
  const [selectedWorkOrder, setSelectedWorkOrder] = usePersistentState("po_pay_wo", workOrderFromUrl);

  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = usePersistentState("po_pay_search", searchFromUrl);
  const [statusFilter, setStatusFilter] = usePersistentState("po_pay_status", "All");
  const [currentPage, setCurrentPage] = usePersistentState("po_pay_page", 1);
  const itemsPerPage = 10;

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter, selectedProject, selectedWorkOrder]);

  // Payment Modal State
  const [selectedPO, setSelectedPO] = useState(null);
  const [paidAmount, setPaidAmount] = useState("");
  const [paymentReference, setPaymentReference] = useState("");
  const [paymentRemarks, setPaymentRemarks] = useState("");
  const [additionalPaymentType, setAdditionalPaymentType] = useState("");
  const [additionalPaymentAmount, setAdditionalPaymentAmount] = useState("");
  const [additionalReceiptFile, setAdditionalReceiptFile] = useState(null);
  const [savingPayment, setSavingPayment] = useState(false);

  // Remarks Modal State
  const [remarkPO, setRemarkPO] = useState(null);
  const [remarkText, setRemarkText] = useState("");
  const [savingRemark, setSavingRemark] = useState(false);

  // Fetch work orders and projects for dropdowns
  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        const [woRes, projRes] = await Promise.all([
          api.get("/work-orders?limit=1000"),
          api.get("/projects?limit=1000"),
        ]);
        if (woRes.data?.success) setWorkOrders(woRes.data.data || []);
        if (projRes.data?.success) setProjects(projRes.data.data || []);
      } catch (err) {
        console.error("Failed to fetch dropdown data", err);
      }
    };
    fetchDropdownData();
  }, []);

  const availableWorkOrders = useMemo(() => {
    if (!selectedProject) return workOrders;
    return workOrders.filter((wo) => {
      const pId = wo.project?._id || (typeof wo.project === "string" ? wo.project : null);
      return String(pId) === String(selectedProject);
    });
  }, [workOrders, selectedProject]);

  useEffect(() => {
    if (workOrderFromUrl) {
      setSelectedWorkOrder(workOrderFromUrl);
    }
  }, [workOrderFromUrl]);

  useEffect(() => {
    if (searchFromUrl) {
      setSearchQuery(searchFromUrl);
    }
  }, [searchFromUrl]);

  // Load Approved Purchase Orders
  const loadPurchaseOrders = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ limit: "1000" });
      if (selectedWorkOrder) params.append("workOrder", selectedWorkOrder);

      const res = await api.get(`/purchase-orders?${params.toString()}`);
      if (res.data.success) {
        const allPOs = res.data.data || [];
        // Auto-reflect all approved / issued POs (exclude drafts if any)
        const approvedPOs = allPOs.filter(
          (po) =>
            po.status !== "Draft" ||
            Boolean(po.approvedBy) ||
            Boolean(po.approvalDate)
        );
        setPurchaseOrders(approvedPOs.length > 0 ? approvedPOs : allPOs);
      }
    } catch (error) {
      swal("Error", "Failed to load approved purchase orders", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPurchaseOrders();
  }, [selectedWorkOrder]);

  // Calculations for Payment Preview
  const paymentPreview = selectedPO
    ? (() => {
        const total = Math.round(Number(selectedPO.totalAmount || 0) * 100) / 100;
        const currentPaid = Math.round(Number(selectedPO.paidAmount || 0) * 100) / 100;
        const nextPaid = Math.round((currentPaid + Number(paidAmount || 0)) * 100) / 100;
        const remaining = Math.max(Math.round((total - currentPaid) * 100) / 100, 0);
        const nextRemaining = Math.max(Math.round((total - nextPaid) * 100) / 100, 0);
        const addlAmt = Number(additionalPaymentAmount || 0);
        const nextStatus =
          nextPaid >= total - 0.5 && total > 0
            ? "Paid"
            : nextPaid > 0
            ? "Partially Paid"
            : "Pending";

        return {
          total,
          currentPaid,
          nextPaid,
          remaining,
          nextRemaining,
          nextStatus,
          addlAmt,
        };
      })()
    : null;

  // Open Payment Modal
  const openPayment = (po) => {
    setSelectedPO(po);
    setPaidAmount(""); // Do not pre-fill default payment amount
    setPaymentReference(po.paymentReference || "");
    setPaymentRemarks(po.paymentRemarks || "");
    setAdditionalPaymentType("");
    setAdditionalPaymentAmount("");
    setAdditionalReceiptFile(null);
  };

  // Save Payment
    // Open Remarks Modal
  const openRemarks = (po) => {
    setRemarkPO(po);
    setRemarkText(po.accountRemarks || po.paymentRemarks || "");
  };

  // Save Remarks
  const saveRemark = async () => {
    if (!remarkPO) return;
    try {
      setSavingRemark(true);
      const res = await api.patch(`/purchase-orders/${remarkPO._id}/remarks`, {
        remarks: remarkText,
        accountRemarks: remarkText,
      });

      if (res.data.success) {
        swal("Success", "Account remark updated successfully", "success");
        setPurchaseOrders((prev) =>
          prev.map((item) =>
            item._id === remarkPO._id
              ? {
                  ...item,
                  accountRemarks: remarkText,
                  paymentRemarks: remarkText,
                  accountRemarksHistory:
                    res.data.data?.accountRemarksHistory || item.accountRemarksHistory,
                }
              : item
          )
        );
        setRemarkPO(null);
      }
    } catch (error) {
      swal("Error", error.response?.data?.message || "Failed to update remarks", "error");
    } finally {
      setSavingRemark(false);
    }
  };

  const savePayment = async () => {
    if (!selectedPO) return;
    const total = Math.round(Number(selectedPO.totalAmount || 0) * 100) / 100;
    const nextPaid = Math.round(Number(paidAmount || 0) * 100) / 100;
    const addlAmt = Math.round(Number(additionalPaymentAmount || 0) * 100) / 100;
    const currentPaid = Math.round(Number(selectedPO.paidAmount || 0) * 100) / 100;
    const remaining = Math.max(Math.round((total - currentPaid) * 100) / 100, 0);

    if (nextPaid <= 0 && addlAmt <= 0) {
      return swal("Error", "Please enter a valid paid amount or additional payment amount", "error");
    }
    // Only compare nextPaid against remaining balance, with 0.50 tolerance for floating-point precision.
    // Additional payment amount is completely separate and is NOT restricted by PO remaining balance.
    if (nextPaid > remaining + 0.5) {
      return swal("Error", "Paid amount cannot exceed the remaining balance", "error");
    }

    try {
      setSavingPayment(true);
      const res = await api.patch(`/purchase-orders/${selectedPO._id}/payment`, {
        paidAmount: nextPaid,
        paymentReference,
        paymentRemarks,
        additionalPaymentType,
        additionalPaymentAmount: addlAmt,
      });
      if (res.data.success) {
        // If an additional receipt file was selected, upload it immediately
        if (additionalReceiptFile) {
          try {
            const formData = new FormData();
            formData.append("receipt", additionalReceiptFile);
            const paymentId = res.data.payment?._id;
            const uploadQuery = paymentId
              ? `?paymentId=${paymentId}&receiptType=additional`
              : `?receiptType=additional`;
            await api.patch(`/purchase-orders/${selectedPO._id}/upload-receipt${uploadQuery}`, formData, {
              headers: { "Content-Type": "multipart/form-data" },
            });
          } catch (fileErr) {
            console.error("Additional receipt upload error:", fileErr);
          }
        }

        swal("Success", "PO Payment recorded successfully", "success");
        setSelectedPO(null);
        setAdditionalReceiptFile(null);
        loadPurchaseOrders();
      }
    } catch (error) {
      swal("Error", error.response?.data?.message || "Failed to update payment", "error");
    } finally {
      setSavingPayment(false);
    }
  };

  // Filtered List
  const filteredPOs = useMemo(() => {
    return purchaseOrders.filter((po) => {
      if (selectedProject) {
        const poProjId = po.project?._id || (typeof po.project === "string" ? po.project : null)
          || po.workOrder?.project?._id || (typeof po.workOrder?.project === "string" ? po.workOrder?.project : null);
        const matchingWo = workOrders.find((w) => String(w._id) === String(po.workOrder?._id || po.workOrder));
        const matchedProjId = poProjId || matchingWo?.project?._id || matchingWo?.project;
        if (String(matchedProjId) !== String(selectedProject)) {
          return false;
        }
      }
      if (selectedWorkOrder) {
        const poWoId = String(po.workOrder?._id || po.workOrder || "");
        if (poWoId !== String(selectedWorkOrder)) {
          return false;
        }
      }
      const q = searchQuery.toLowerCase().trim();
      const poNum = (po.poNumber || "").toLowerCase();
      const vName = (po.vendor?.name || po.vendor?.companyName || po.vendor?.vendorName || "").toLowerCase();
      const woNum = (po.workOrder?.woNo || po.workOrder?.woNumber || po.workOrder?.workOrderNumber || po.workOrderNumber || "").toLowerCase();
      const woLbl = (po.workOrder?.label || "").toLowerCase();

      const matchesSearch =
        !q ||
        poNum.includes(q) ||
        vName.includes(q) ||
        woNum.includes(q) ||
        woLbl.includes(q);

      const paymentStatus =
        po.paymentStatus ||
        (po.paidAmount >= po.totalAmount
          ? "Paid"
          : Number(po.paidAmount || 0) > 0
          ? "Partially Paid"
          : "Pending");

      const matchesStatus =
        statusFilter === "All" || paymentStatus === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [purchaseOrders, searchQuery, statusFilter, selectedProject, selectedWorkOrder, workOrders]);

  const totalCount = filteredPOs.length;
  const totalPages = Math.ceil(totalCount / itemsPerPage) || 1;
  const paginatedPOs = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredPOs.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredPOs, currentPage, itemsPerPage]);

  return (
    <Fragment>
      <PageTitle activeMenu="PO Payments" motherMenu="Accounts" />

      {/* ===== FILTER & SEARCH BAR ===== */}
      <Card className="shadow-sm border-0 mb-4">
        <Card.Body>
          <Row className="g-3 align-items-center">
            <Col md={3}>
              <Form.Label className="small fw-semibold mb-1">Search</Form.Label>
              <Form.Control
                type="text"
                placeholder="Search PO, Vendor, Work Order..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </Col>
            <Col md={3}>
              <Form.Label className="small fw-semibold mb-1">Project</Form.Label>
              <Form.Select
                value={selectedProject}
                onChange={(e) => {
                  setSelectedProject(e.target.value);
                  setSelectedWorkOrder("");
                }}
              >
                <option value="">All Projects</option>
                {projects.map((p) => (
                  <option key={p._id} value={p._id}>
                    {p.projectName || p.name || p.projectCode || p._id}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Label className="small fw-semibold mb-1">Work Order</Form.Label>
              <Form.Select
                value={selectedWorkOrder}
                onChange={(e) => setSelectedWorkOrder(e.target.value)}
              >
                <option value="">All Work Orders</option>
                {availableWorkOrders.map((wo) => (
                  <option key={wo._id} value={wo._id}>
                    {wo.woNo || wo.woNumber}{wo.label ? ` – ${wo.label}` : ""}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Label className="small fw-semibold mb-1">Payment Status</Form.Label>
              <Form.Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="All">All Payment Statuses</option>
                <option value="Pending">Pending</option>
                <option value="Partially Paid">Partially Paid</option>
                <option value="Paid">Paid</option>
              </Form.Select>
            </Col>
            <Col md={1} className="d-flex align-items-end">
              <Button
                variant="outline-secondary"
                className="w-100 mt-4 d-flex align-items-center justify-content-center"
                style={{ height: "38px" }}
                title="Reset Filters"
                onClick={() => {
                  setSearchQuery("");
                  setStatusFilter("All");
                  setSelectedProject("");
                  setSelectedWorkOrder("");
                }}
              >
                <i className="fas fa-undo" />
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* ===== PURCHASE ORDERS TABLE ===== */}
      <Card className="shadow-sm border-0">
        <Card.Header className="bg-white border-0 py-3 d-flex justify-content-between align-items-center">
          <Card.Title className="mb-0 fs-5 fw-bold text-dark">
            Approved Purchase Order Payments
            {selectedWorkOrder && (
              <Badge bg="secondary" className="ms-2">
                WO Filtered
              </Badge>
            )}
          </Card.Title>
          <span className="text-muted small">
            Showing <strong>{filteredPOs.length}</strong> of <strong>{purchaseOrders.length}</strong> records
          </span>
        </Card.Header>
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : (
            <div className="table-responsive">
              <Table hover className="align-middle mb-0">
                <thead className="table-light">
                    <tr>
                      <th>PO Number</th>
                      <th>Work Order</th>
                      <th>Vendor</th>
                      <th>Total Amount</th>
                      <th>Paid Amount</th>
                      <th>Balance</th>
                      <th>Status</th>
                      <th>Receipt</th>
                      <th>Addl. Receipt</th>
                      <th>Signed Copy</th>
                      <th className="text-end text-nowrap" style={{ minWidth: "220px" }}>Action</th>
                    </tr>
                </thead>
                <tbody>
                  {paginatedPOs.map((po) => {
                    const total = Number(po.totalAmount || 0);
                    const paid = Number(po.paidAmount || 0);
                    const balance = Math.max(total - paid, 0);

                    const paymentStatus =
                      po.paymentStatus ||
                      (paid >= total && total > 0
                        ? "Paid"
                        : paid > 0
                        ? "Partially Paid"
                        : "Pending");

                    const woNumber =
                      po.workOrder?.woNo ||
                      po.workOrder?.woNumber ||
                      po.workOrder?.workOrderNumber ||
                      po.workOrderNumber ||
                      "";

                    const vendorName =
                      po.vendor?.name ||
                      po.vendor?.companyName ||
                      po.vendor?.vendorName ||
                      "-";

                    const dateDisplay = po.approvalDate || po.issueDate || po.createdAt;

                    return (
                      <tr key={po._id}>
                        <td>
                          <strong className="text-primary">{po.poNumber}</strong>
                          {dateDisplay && (
                            <div className="small text-muted">
                              {new Date(dateDisplay).toLocaleDateString("en-IN")}
                            </div>
                          )}
                        {(po.accountRemarks || po.paymentRemarks) && (
                            <div
                              className="small text-muted mt-1 p-1 px-2 rounded bg-light border text-truncate"
                              style={{ maxWidth: "210px", fontSize: "0.75rem", cursor: "pointer" }}
                              title={`Click to view/edit: ${po.accountRemarks || po.paymentRemarks}`}
                              onClick={() => openRemarks(po)}
                            >
                              <i className="far fa-comment-alt text-info me-1"></i>
                              {po.accountRemarks || po.paymentRemarks}
                            </div>
                          )}
                        </td>
                        <td>
                          <div className="fw-semibold">{woNumber || "-"}</div>
                          {po.workOrder?.label && (
                            <div className="small text-muted">{po.workOrder.label}</div>
                          )}
                        </td>
                        <td>{vendorName}</td>
                        <td className="fw-semibold">{money(total)}</td>
                        <td className="text-success fw-semibold">{money(paid)}</td>
                        <td className={balance > 0 ? "text-danger fw-semibold" : "text-muted"}>
                          {money(balance)}
                        </td>
                        <td>
                          <Badge bg={statusVariant(paymentStatus)}>
                            {paymentStatus}
                          </Badge>
                        </td>
                        <td>
                          <div className="d-flex flex-column gap-1">
                            {getPaymentRows(po).map((payment, index) => (
                              <div key={payment._id || index} className="d-flex align-items-center gap-1">
                                <span className="small text-muted text-nowrap">
                                  P{index + 1} {money(payment.amount)}
                                </span>
                                <ReceiptActions
                                  id={po._id}
                                  type="purchase-orders"
                                  receipt={payment.receipt}
                                  query={payment.legacy ? "" : `?paymentId=${payment._id}`}
                                  canUpload={true}
                                  onUploadSuccess={loadPurchaseOrders}
                                  title="Receipt"
                                />
                              </div>
                            ))}
                            {!getPaymentRows(po).length && <span className="text-muted">-</span>}
                          </div>
                        </td>
                        <td>
                          <div className="d-flex flex-column gap-1">
                            {getPaymentRows(po)
                              .filter((p) => Number(p.additionalPaymentAmount) > 0 || p.additionalPaymentReceipt)
                              .map((payment, index) => (
                                <div key={payment._id || index} className="d-flex align-items-center gap-1">
                                  <span className="small text-muted text-nowrap" title={`${payment.additionalPaymentType || "Additional"}: ${money(payment.additionalPaymentAmount)}`}>
                                    <span className="badge bg-light text-dark border me-1" style={{ fontSize: "0.68rem", padding: "1px 4px" }}>
                                      {payment.additionalPaymentType || "Addl"}
                                    </span>
                                    {money(payment.additionalPaymentAmount)}
                                  </span>
                                  <ReceiptActions
                                    id={po._id}
                                    type="purchase-orders"
                                    receipt={payment.additionalPaymentReceipt}
                                    query={payment.legacy ? "?receiptType=additional" : `?paymentId=${payment._id}&receiptType=additional`}
                                    canUpload={true}
                                    onUploadSuccess={loadPurchaseOrders}
                                    title="Additional Receipt"
                                  />
                                </div>
                              ))}
                            {!getPaymentRows(po).some((p) => Number(p.additionalPaymentAmount) > 0 || p.additionalPaymentReceipt) && (
                              <span className="text-muted">-</span>
                            )}
                          </div>
                        </td>
                        <td>
                          <SignedBillActions
                            id={po._id}
                            type="purchase-orders"
                            signedCopy={po.signedCopy}
                            canUpload={true}
                            onUploadSuccess={loadPurchaseOrders}
                          />
                        </td>
                        <td className="text-end align-middle" style={{ minWidth: "220px" }}>
                          <div className="d-inline-flex align-items-center gap-2 justify-content-end">
                            {paymentStatus === "Paid" ? (
                              <span
                                className="btn btn-sm btn-outline-success disabled text-nowrap d-inline-flex align-items-center gap-1"
                                style={{
                                  opacity: 1,
                                  pointerEvents: "none",
                                  fontWeight: 600,
                                  backgroundColor: "#f0fdf4",
                                  borderColor: "#22c55e",
                                  color: "#16a34a",
                                }}
                              >
                                <i className="fas fa-check-circle text-success"></i>
                                <span>Paid</span>
                              </span>
                            ) : (
                              <Button
                                size="sm"
                                variant="outline-primary"
                                className="text-nowrap d-inline-flex align-items-center gap-1"
                                onClick={() => openPayment(po)}
                              >
                                Review / Mark Paid
                              </Button>
                            )}

                            <Button
                              size="sm"
                              variant={po.accountRemarks || po.paymentRemarks ? "outline-info" : "outline-secondary"}
                              className="text-nowrap d-inline-flex align-items-center gap-1"
                              onClick={() => openRemarks(po)}
                              title={po.accountRemarks || po.paymentRemarks ? `Remark: ${po.accountRemarks || po.paymentRemarks}` : "Add Remark"}
                            >
                              <i className="far fa-comment-dots"></i>
                              <span>Remark</span>
                              {(po.accountRemarks || po.paymentRemarks) && (
                                <span
                                  className="badge bg-info text-white rounded-circle d-inline-flex align-items-center justify-content-center"
                                  style={{ width: "16px", height: "16px", fontSize: "0.62rem", padding: 0 }}
                                >
                                  ✓
                                </span>
                              )}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {filteredPOs.length === 0 && (
                    <tr>
                      <td colSpan="11" className="text-center text-muted py-5">
                        No approved purchase orders found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          )}
          {!loading && filteredPOs.length > 0 && (
            <AppPagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalCount={totalCount}
              itemsLength={paginatedPOs.length}
              limit={itemsPerPage}
              onPageChange={(page) => setCurrentPage(page)}
            />
          )}
        </Card.Body>
      </Card>

      {/* ===== RECORD PAYMENT MODAL ===== */}
      <Modal
        show={!!selectedPO}
        onHide={() => setSelectedPO(null)}
        centered
        scrollable
        size="lg"
        dialogClassName="modal-850w"
      >
        <Modal.Header closeButton className="border-bottom py-3 px-4 bg-light">
          <div>
            <Modal.Title className="fs-5 fw-bold mb-0">
              <i className="fas fa-file-invoice-dollar text-primary me-2"></i>
              Purchase Order Payment
            </Modal.Title>
            {selectedPO && (
              <div className="small text-muted mt-1">
                PO: <strong className="text-dark">{selectedPO.poNumber}</strong>
                <span className="mx-2">·</span>
                Vendor:{" "}
                <strong className="text-dark">
                  {selectedPO.vendor?.name ||
                    selectedPO.vendor?.companyName ||
                    selectedPO.vendor?.vendorName ||
                    "-"}
                </strong>
              </div>
            )}
          </div>
        </Modal.Header>
        <Modal.Body className="p-3 p-md-4">
          {selectedPO && (
            <>
              <Row className="g-3">
                {/* Left Column: PO & Financial Overview */}
                <Col md={5}>
                  <div className="border rounded p-3 bg-light mb-3">
                    <div className="small text-muted text-uppercase fw-semibold mb-2" style={{ letterSpacing: "0.5px" }}>
                      Work Order Info
                    </div>
                    <div className="small">
                      <span className="text-muted">Work Order:</span>{" "}
                      <strong>
                        {selectedPO.workOrder?.woNo ||
                          selectedPO.workOrder?.woNumber ||
                          selectedPO.workOrder?.workOrderNumber ||
                          "-"}
                      </strong>
                      {selectedPO.workOrder?.label && (
                        <div className="text-muted small mt-1">{selectedPO.workOrder.label}</div>
                      )}
                    </div>
                  </div>

                  {paymentPreview && (
                    <div className="border rounded p-3 bg-white shadow-sm">
                      <div className="small text-muted text-uppercase fw-semibold mb-2" style={{ letterSpacing: "0.5px" }}>
                        Financial Balance
                      </div>
                      <div className="d-flex justify-content-between mb-1 small">
                        <span>PO Total:</span>
                        <strong>{money(paymentPreview.total)}</strong>
                      </div>
                      <div className="d-flex justify-content-between mb-1 small">
                        <span>Already Paid:</span>
                        <strong className="text-success">{money(paymentPreview.currentPaid)}</strong>
                      </div>
                      <div className="d-flex justify-content-between mb-1 small">
                        <span>Current Balance:</span>
                        <strong className="text-danger">{money(paymentPreview.remaining)}</strong>
                      </div>
                      <hr className="my-2" />
                      <div className="d-flex justify-content-between mb-1 small">
                        <span>Paid After Save:</span>
                        <strong className="text-primary">{money(paymentPreview.nextPaid)}</strong>
                      </div>
                      <div className="d-flex justify-content-between mb-1 small">
                        <span>Remaining Balance:</span>
                        <strong className={paymentPreview.nextRemaining > 0 ? "text-warning" : "text-success"}>
                          {money(paymentPreview.nextRemaining)}
                        </strong>
                      </div>
                      {paymentPreview.addlAmt > 0 && (
                        <div className="d-flex justify-content-between mb-1 small text-info">
                          <span>+ Additional ({additionalPaymentType || "Extra"}):</span>
                          <strong>+{money(paymentPreview.addlAmt)}</strong>
                        </div>
                      )}
                      <div className="d-flex justify-content-between align-items-center mt-2 pt-2 border-top small">
                        <span>Status After Save:</span>
                        <Badge bg={statusVariant(paymentPreview.nextStatus)}>
                          {paymentPreview.nextStatus}
                        </Badge>
                      </div>
                    </div>
                  )}
                </Col>

                {/* Right Column: Payment Form */}
                <Col md={7}>
                  <div className="border rounded p-3 bg-white shadow-sm h-100 d-flex flex-column justify-content-between">
                    <div>
                      <div className="small text-muted text-uppercase fw-semibold mb-3" style={{ letterSpacing: "0.5px" }}>
                        Record Payment
                      </div>
                      <Row className="g-2">
                        <Col sm={6}>
                          <Form.Group className="mb-2">
                            <Form.Label className="small fw-bold mb-1">
                              Paid Amount (₹) <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                              type="number"
                              min="0"
                              step="any"
                              max={paymentPreview ? Math.ceil(paymentPreview.remaining) : undefined}
                              value={paidAmount}
                              onChange={(e) => setPaidAmount(e.target.value)}
                              placeholder="0.00"
                            />
                          </Form.Group>
                        </Col>
                        <Col sm={6}>
                          <Form.Group className="mb-2">
                            <Form.Label className="small fw-bold mb-1">Payment Ref / UTR</Form.Label>
                            <Form.Control
                              placeholder="e.g. UTR12345678"
                              value={paymentReference}
                              onChange={(e) => setPaymentReference(e.target.value)}
                            />
                          </Form.Group>
                        </Col>
                        <Col sm={6}>
                          <Form.Group className="mb-2">
                            <Form.Label className="small fw-bold mb-1">Additional Type</Form.Label>
                            <Form.Control
                              type="text"
                              placeholder="e.g. Freight, Advance"
                              value={additionalPaymentType}
                              onChange={(e) => setAdditionalPaymentType(e.target.value)}
                            />
                          </Form.Group>
                        </Col>
                        <Col sm={6}>
                          <Form.Group className="mb-2">
                            <Form.Label className="small fw-bold mb-1">Additional Amount (₹)</Form.Label>
                            <Form.Control
                              type="number"
                              min="0"
                              step="any"
                              placeholder="0.00"
                              value={additionalPaymentAmount}
                              onChange={(e) => setAdditionalPaymentAmount(e.target.value)}
                            />
                          </Form.Group>
                        </Col>
                        <Col sm={12}>
                          <Form.Group className="mb-2">
                            <Form.Label className="small fw-bold mb-1">Additional Payment Receipt</Form.Label>
                            <Form.Control
                              type="file"
                              size="sm"
                              accept="image/*,application/pdf"
                              onChange={(e) => setAdditionalReceiptFile(e.target.files?.[0] || null)}
                            />
                            {additionalReceiptFile && (
                              <div className="small text-success mt-1">
                                <i className="fas fa-paperclip me-1"></i>
                                {additionalReceiptFile.name}
                              </div>
                            )}
                          </Form.Group>
                        </Col>
                        <Col sm={12}>
                          <Form.Group className="mb-2">
                            <Form.Label className="small fw-bold mb-1">Payment Remarks</Form.Label>
                            <Form.Control
                              as="textarea"
                              rows={2}
                              placeholder="Additional remarks or notes..."
                              value={paymentRemarks}
                              onChange={(e) => setPaymentRemarks(e.target.value)}
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                    </div>

                    <div className="border rounded p-2 bg-light small text-muted mt-2">
                      <i className="fas fa-info-circle text-primary me-1"></i>
                      Save this payment first. After saving, receipt upload will appear beside the installment in Payment History.
                    </div>
                  </div>
                </Col>
              </Row>

              {/* Bottom: Payment History */}
              <div className="border rounded p-3 mt-3 bg-white">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <div className="fw-semibold small text-uppercase text-muted" style={{ letterSpacing: "0.5px" }}>
                    Payment History
                  </div>
                  <span className="badge bg-light text-dark border">
                    {getPaymentRows(selectedPO).length} Installment{getPaymentRows(selectedPO).length === 1 ? "" : "s"}
                  </span>
                </div>
                <div style={{ maxHeight: "170px", overflowY: "auto" }}>
                  {getPaymentRows(selectedPO).map((payment, index) => (
                    <div
                      key={payment._id || index}
                      className="d-flex justify-content-between align-items-center border-top py-2 px-1 gap-2"
                    >
                      <div>
                        <div className="fw-semibold">
                          {money(payment.amount)}
                          {Number(payment.additionalPaymentAmount) > 0 && (
                            <span className="badge bg-light text-dark ms-2 fw-normal border">
                              +{payment.additionalPaymentType ? `${payment.additionalPaymentType}: ` : "Additional: "}
                              {money(payment.additionalPaymentAmount)}
                            </span>
                          )}
                        </div>
                        <div className="small text-muted">
                          {payment.paymentDate ? new Date(payment.paymentDate).toLocaleDateString("en-IN") : "-"}
                          {payment.paymentReference ? ` · ${payment.paymentReference}` : ""}
                          {payment.paymentRemarks ? ` · "${payment.paymentRemarks}"` : ""}
                        </div>
                      </div>
                      <div className="d-flex align-items-center gap-2">
                        <div className="d-flex flex-column align-items-end gap-1">
                          <div className="d-flex align-items-center gap-1">
                            <span className="small text-muted me-1" style={{ fontSize: "0.72rem" }}>Receipt:</span>
                            <ReceiptActions
                              id={selectedPO._id}
                              type="purchase-orders"
                              receipt={payment.receipt}
                              query={payment.legacy ? "" : `?paymentId=${payment._id}`}
                              onUploadSuccess={loadPurchaseOrders}
                              title="Payment Receipt"
                            />
                          </div>
                          {(Number(payment.additionalPaymentAmount) > 0 || payment.additionalPaymentReceipt) && (
                            <div className="d-flex align-items-center gap-1">
                              <span className="small text-muted me-1" style={{ fontSize: "0.72rem" }}>Addl Receipt:</span>
                              <ReceiptActions
                                id={selectedPO._id}
                                type="purchase-orders"
                                receipt={payment.additionalPaymentReceipt}
                                query={payment.legacy ? "?receiptType=additional" : `?paymentId=${payment._id}&receiptType=additional`}
                                onUploadSuccess={loadPurchaseOrders}
                                title="Additional Receipt"
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  {!getPaymentRows(selectedPO).length && (
                    <div className="small text-muted py-2 text-center">No payment installments recorded yet.</div>
                  )}
                </div>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer className="border-top py-2 px-4 bg-light">
          <Button variant="outline-secondary" onClick={() => setSelectedPO(null)}>
            Cancel
          </Button>
          <Button variant="primary" className="px-4" onClick={savePayment} disabled={savingPayment}>
            {savingPayment ? (
              <>
                <Spinner animation="border" size="sm" className="me-1" />
                Saving...
              </>
            ) : (
              "Save Payment"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
      {/* ===== ACCOUNT REMARKS MODAL ===== */}
      <Modal show={!!remarkPO} onHide={() => setRemarkPO(null)} centered scrollable>
        <Modal.Header closeButton>
          <Modal.Title className="fs-5 fw-bold">
            <i className="far fa-comment-dots text-primary me-2"></i>
            Accounts Remark - {remarkPO?.poNumber}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {remarkPO && (
            <>
              <div className="border rounded p-3 mb-3 bg-light">
                <Row className="g-2 small">
                  <Col xs={6}>
                    <span className="text-muted">PO Number:</span>{" "}
                    <strong>{remarkPO.poNumber}</strong>
                  </Col>
                  <Col xs={6}>
                    <span className="text-muted">Status:</span>{" "}
                    <Badge bg={statusVariant(remarkPO.paymentStatus || (Number(remarkPO.paidAmount || 0) >= Number(remarkPO.totalAmount || 0) && Number(remarkPO.totalAmount || 0) > 0 ? "Paid" : Number(remarkPO.paidAmount || 0) > 0 ? "Partially Paid" : "Pending"))}>
                      {remarkPO.paymentStatus || (Number(remarkPO.paidAmount || 0) >= Number(remarkPO.totalAmount || 0) && Number(remarkPO.totalAmount || 0) > 0 ? "Paid" : Number(remarkPO.paidAmount || 0) > 0 ? "Partially Paid" : "Pending")}
                    </Badge>
                  </Col>
                  <Col xs={6}>
                    <span className="text-muted">Vendor:</span>{" "}
                    <strong>
                      {remarkPO.vendor?.name ||
                        remarkPO.vendor?.companyName ||
                        remarkPO.vendor?.vendorName ||
                        "-"}
                    </strong>
                  </Col>
                  <Col xs={6}>
                    <span className="text-muted">Total / Balance:</span>{" "}
                    <strong>
                      {money(remarkPO.totalAmount)} /{" "}
                      <span className="text-danger">
                        {money(Math.max(Number(remarkPO.totalAmount || 0) - Number(remarkPO.paidAmount || 0), 0))}
                      </span>
                    </strong>
                  </Col>
                </Row>
              </div>

              <Form.Group className="mb-3">
                <Form.Label className="small fw-bold">
                  Accounts Remark / Notes
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="Enter remarks regarding payment schedule, hold, vendor verification, cheque date, etc..."
                  value={remarkText}
                  onChange={(e) => setRemarkText(e.target.value)}
                  autoFocus
                />
              </Form.Group>

              {/* Quick Preset Buttons */}
              <div className="mb-3">
                <div className="small text-muted mb-1 fw-semibold">Quick Suggestions:</div>
                <div className="d-flex flex-wrap gap-1">
                  {[
                    "Awaiting Vendor Invoice",
                    "Bank Details Pending",
                    "Under Verification",
                    "Payment Scheduled",
                    "Hold on Payment",
                    "Partially Processed",
                  ].map((preset) => (
                    <Button
                      key={preset}
                      variant="outline-secondary"
                      size="sm"
                      className="py-0 px-2"
                      style={{ fontSize: "0.75rem", borderRadius: "12px" }}
                      onClick={() => {
                        setRemarkText((prev) =>
                          prev ? `${prev.trim()} · ${preset}` : preset
                        );
                      }}
                    >
                      + {preset}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Remarks History / Audit Trail */}
              {Array.isArray(remarkPO.accountRemarksHistory) &&
                remarkPO.accountRemarksHistory.length > 0 && (
                  <div className="border rounded p-3 mt-3">
                    <div className="fw-semibold small text-muted text-uppercase mb-2">
                      <i className="fas fa-history me-1"></i> Remarks History
                    </div>
                    <div className="d-flex flex-column gap-2" style={{ maxHeight: "180px", overflowY: "auto" }}>
                      {[...remarkPO.accountRemarksHistory].reverse().map((entry, idx) => (
                        <div key={idx} className="p-2 rounded bg-light border-start border-3 border-primary small">
                          <div className="fw-semibold text-dark">{entry.remark}</div>
                          <div className="text-muted" style={{ fontSize: "0.72rem" }}>
                            {entry.addedByName || "Accounts"} ·{" "}
                            {entry.createdAt ? new Date(entry.createdAt).toLocaleString("en-IN") : "-"}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={() => setRemarkPO(null)}>
            Close
          </Button>
          <Button variant="primary" onClick={saveRemark} disabled={savingRemark}>
            {savingRemark ? (
              <>
                <Spinner animation="border" size="sm" className="me-1" />
                Saving...
              </>
            ) : (
              "Save Remark"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Fragment>
  );
};

export default PurchaseOrderPaymentList;
